import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Divider, Typography, message } from "antd";
import { Formik } from "formik";
import * as Yup from "yup";
import FormInput from "components/FormItems/FormInput";
import FormSelectInput from "components/FormItems/FormSelectInput";
import AppTable from "components/AppTable/AppTable";
import AppButton from "components/AppButton/AppButton";
import { inject, observer } from "mobx-react";
import { fetchCurrencies } from "service/userService";
import { PAYMENT_MODE_OPTIONS, PROVIDER_OPTIONS, SERVICE_TYPE_OPTIONS } from "constant/commonList";

// Validation schemas
const dedicatedNumberValidationSchema = Yup.object().shape({
  number: Yup.string()
    .matches(/^[0-9]+$/, "Please enter only numbers")
    .min(10, "Number must be at least 10 digits")
    .required("Number is required"),
  amount: Yup.number().min(0, "Amount must be positive"),
  country: Yup.string().required("Country is required")
});

const businessNameValidationSchema = Yup.object().shape({
  businessName: Yup.string()
    .min(2, "Business name must be at least 2 characters")
    .required("Business name is required")
});

const personalNumberValidationSchema = Yup.object().shape({
  personalNumber: Yup.string()
    .matches(/^[0-9]+$/, "Please enter only numbers")
    .min(10, "Number must be at least 10 digits")
    .required("Personal number is required")
});

const SenderIdForm = ({ user, readMode = false, userStore, userSenderIds }) => {
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Current state of all data
  const [currentData, setCurrentData] = useState({
    mobileSenders: [],
    businessNames: [],
    personalNumbers: []
  });

  // Helper function to safely parse array data
  const safeParseArray = useCallback(data => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Error parsing array data:", e);
        return [];
      }
    }
    return [];
  }, []);

  // Initialize data on component mount or when userSenderIds changes
  useEffect(() => {
    const mobileSenders = safeParseArray(userSenderIds?.mobileSenders);
    const businessNames = safeParseArray(userSenderIds?.senders);
    const personalNumbers = safeParseArray(userSenderIds?.personalSenders);

    const data = {
      mobileSenders,
      businessNames,
      personalNumbers
    };
    setCurrentData(data);
  }, [userSenderIds, safeParseArray]);

  // Fetch countries on component mount
  useEffect(() => {
    fetchCountries();
    userStore.getUserSenderIds(user.id);
  }, []);

  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await fetchCurrencies();
      const countryOptions = response.data.map(country => ({
        label: country.country,
        value: country._id,
        code: country.code || country.country,
        id: country._id
      }));
      setCountries(countryOptions);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Dedicated Number Form Handlers
  const handleAddDedicatedNumber = async (values, { setSubmitting, resetForm }) => {
    try {
      const selectedCountry = countries.find(c => c.value === values.country);
      if (!selectedCountry) {
        message.warning("Please select a country");
        return;
      }

      const newItem = {
        ...values,
        country: selectedCountry.id
      };

      const updatedMobileSenders = [...currentData.mobileSenders, newItem];
      const passdata = {
        mobileSenders: updatedMobileSenders,
        senders: currentData.businessNames,
        personalSenders: currentData.personalNumbers
      };

      userStore.updateUserSenderIds(user.id, passdata);
      setCurrentData(prev => ({
        ...prev,
        mobileSenders: updatedMobileSenders
      }));

      message.success("Dedicated number added successfully");
      resetForm();
    } catch (error) {
      console.error("Error adding dedicated number:", error);
      message.error("Failed to add dedicated number");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddBusinessName = async (values, { setSubmitting, resetForm }) => {
    try {
      const businessName = values.businessName.trim();

      if (currentData.businessNames.includes(businessName)) {
        message.warning("Business name already exists");
        return;
      }

      const updatedBusinessNames = [...currentData.businessNames, businessName];
      const response = {
        mobileSenders: currentData.mobileSenders,
        senders: updatedBusinessNames,
        personalSenders: currentData.personalNumbers
      };
      userStore.updateUserSenderIds(user.id, response);
      setCurrentData(prev => ({
        ...prev,
        businessNames: updatedBusinessNames
      }));

      message.success("Business name added successfully");
      resetForm();
    } catch (error) {
      console.error("Error adding business name:", error);
      message.error("Failed to add business name");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPersonalNumber = async (values, { setSubmitting, resetForm }) => {
    try {
      const personalNumber = values.personalNumber.trim();

      if (currentData.personalNumbers.includes(personalNumber)) {
        message.warning("Personal number already exists");
        return;
      }

      const updatedPersonalNumbers = [...currentData.personalNumbers, personalNumber];
      const response = {
        mobileSenders: currentData.mobileSenders,
        senders: currentData.businessNames,
        personalSenders: updatedPersonalNumbers
      };
      userStore.updateUserSenderIds(user.id, response);

      setCurrentData(prev => ({
        ...prev,
        personalNumbers: updatedPersonalNumbers
      }));

      message.success("Personal number added successfully");
      resetForm();
    } catch (error) {
      console.error("Error adding personal number:", error);
      message.error("Failed to add personal number");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete handlers
  const deleteDedicatedNumber = useCallback(
    index => {
      const updatedMobileSenders = currentData.mobileSenders.filter((_, i) => i !== index);

      const response = {
        mobileSenders: updatedMobileSenders,
        senders: currentData.businessNames,
        personalSenders: currentData.personalNumbers
      };
      userStore.updateUserSenderIds(user.id, response);

      setCurrentData(prev => ({
        ...prev,
        mobileSenders: updatedMobileSenders
      }));
      message.success("Dedicated number removed");
    },
    [currentData.mobileSenders]
  );

  const deleteBusinessName = useCallback(
    name => {
      const updatedBusinessNames = currentData.businessNames.filter(item => item !== name);

      const response = {
        mobileSenders: currentData.mobileSenders,
        senders: updatedBusinessNames,
        personalSenders: currentData.personalNumbers
      };
      userStore.updateUserSenderIds(user.id, response);

      setCurrentData(prev => ({
        ...prev,
        businessNames: updatedBusinessNames
      }));
      message.success("Business name removed");
    },
    [currentData.businessNames]
  );

  const deletePersonalNumber = useCallback(
    index => {
      const updatedPersonalNumbers = currentData.personalNumbers.filter((_, i) => i !== index);

      const response = {
        mobileSenders: currentData.mobileSenders,
        senders: currentData.businessNames,
        personalSenders: updatedPersonalNumbers
      };
      userStore.updateUserSenderIds(user.id, response);

      setCurrentData(prev => ({
        ...prev,
        personalNumbers: updatedPersonalNumbers
      }));
      message.success("Personal number removed");
    },
    [currentData.personalNumbers]
  );

  // Helper function to render country info
  const renderCountryInfo = useCallback(
    country => {
      if (!country) return "N/A";
      if (typeof country === "object") {
        return country.label || country.country || "N/A";
      }
      const foundCountry = countries.find(c => c.value === country || c.id === country);
      return foundCountry?.label || "N/A";
    },
    [countries]
  );

  // Table column definitions
  const getDedicatedNumberColumns = useCallback(
    () => [
      {
        title: "Number",
        dataIndex: "number",
        key: "number"
      },
      {
        title: "Provider",
        dataIndex: "provider",
        key: "provider",
        render: text => text?.charAt(0).toUpperCase() + text?.slice(1) || "N/A"
      },
      {
        title: "Service Type",
        dataIndex: "serviceType",
        key: "serviceType",
        render: text => text?.toUpperCase() || "N/A"
      },
      {
        title: "Payment Mode",
        dataIndex: "paymentMode",
        key: "paymentMode",
        render: text => text?.charAt(0).toUpperCase() + text?.slice(1) || "N/A"
      },
      {
        title: "Country",
        dataIndex: "country",
        key: "country",
        render: renderCountryInfo
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: amount => `$${amount || 0}`
      },
      ...(!readMode
        ? [
            {
              title: "Action",
              key: "action",
              render: (text, record, index) => <i className="fas fa-trash-alt" style={{ cursor: "pointer", color: "#ff4d4f" }} onClick={() => deleteDedicatedNumber(index)} />
            }
          ]
        : [])
    ],
    [readMode, renderCountryInfo, deleteDedicatedNumber]
  );

  const getBusinessNameColumns = useCallback(
    () => [
      {
        title: "Business Names",
        dataIndex: "name",
        key: "name",
        render: (text, record, index) => (
          <div className="d-flex align-items-center justify-content-between">
            <span>{text}</span>
            {!readMode && <i className="fas fa-trash-alt" style={{ cursor: "pointer", color: "#ff4d4f" }} onClick={() => deleteBusinessName(text)} />}
          </div>
        )
      }
    ],
    [readMode, deleteBusinessName]
  );

  const getPersonalNumberColumns = useCallback(
    () => [
      {
        title: "Personal Numbers",
        dataIndex: "number",
        key: "number",
        render: (text, record, index) => (
          <div className="d-flex align-items-center justify-content-between">
            <span>{text}</span>
            {!readMode && <i className="fas fa-trash-alt" style={{ cursor: "pointer", color: "#ff4d4f" }} onClick={() => deletePersonalNumber(index)} />}
          </div>
        )
      }
    ],
    [readMode, deletePersonalNumber]
  );

  return (
    <div>
      <Row gutter={20}>
        <Col span={24}>
          <Typography.Title level={4}>Dedicated Numbers</Typography.Title>
        </Col>

        {!readMode && (
          <Col span={24}>
            <Formik
              initialValues={{
                number: "",
                provider: "sinch",
                serviceType: "sms",
                paymentMode: "reward",
                country: "",
                amount: 0
              }}
              validationSchema={dedicatedNumberValidationSchema}
              onSubmit={handleAddDedicatedNumber}
            >
              {({ values, errors, touched, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
                <Row gutter={20}>
                  <Col span={12}>
                    <FormInput
                      containerClassname="mb-0"
                      placeholder="e.g. 614XXXXXXXX"
                      error={errors.number}
                      touched={touched.number}
                      label="Dedicated Number"
                      value={values.number}
                      onChange={handleChange}
                      name="number"
                      size="default"
                    />
                  </Col>
                  <Col span={12}>
                    <FormSelectInput
                      containerClassname="mb-0"
                      label="Provider"
                      name="provider"
                      placeholder="Select Provider"
                      options={PROVIDER_OPTIONS}
                      onChange={value => setFieldValue("provider", value)}
                      value={values.provider}
                      size="default"
                    />
                  </Col>
                  <Col span={12}>
                    <FormSelectInput
                      containerClassname="mb-0"
                      label="Service Type"
                      name="serviceType"
                      placeholder="Select Service Type"
                      options={SERVICE_TYPE_OPTIONS}
                      onChange={value => setFieldValue("serviceType", value)}
                      value={values.serviceType}
                      size="default"
                    />
                  </Col>
                  <Col span={12}>
                    <FormSelectInput
                      containerClassname="mb-0"
                      label="Payment Mode"
                      name="paymentMode"
                      placeholder="Select Payment Mode"
                      options={PAYMENT_MODE_OPTIONS}
                      onChange={value => setFieldValue("paymentMode", value)}
                      value={values.paymentMode}
                      size="default"
                    />
                  </Col>
                  <Col span={12}>
                    <FormSelectInput
                      containerClassname="mb-0"
                      label="Country"
                      name="country"
                      placeholder="Select Country"
                      options={countries}
                      onChange={value => setFieldValue("country", value)}
                      value={values.country}
                      size="default"
                      loading={loadingCountries}
                      error={errors.country}
                      touched={touched.country}
                    />
                  </Col>
                  <Col span={12}>
                    <FormInput
                      containerClassname="mb-0"
                      placeholder="0.00"
                      error={errors.amount}
                      touched={touched.amount}
                      label="Amount ($)"
                      value={values.amount}
                      onChange={handleChange}
                      name="amount"
                      size="default"
                      type="number"
                    />
                  </Col>
                  <Col span={24} style={{ paddingTop: "4px", paddingBottom: "7px" }}>
                    <AppButton label="Add Dedicated Number" light type="button" loading={isSubmitting} prefixIcon={<i className="fas fa-plus me-2" />} onClick={handleSubmit} />
                  </Col>
                </Row>
              )}
            </Formik>
          </Col>
        )}

        <Col span={24}>
          {currentData.mobileSenders.length > 0 && (
            <AppTable columns={getDedicatedNumberColumns()} dataSource={currentData.mobileSenders} pagination={false} bordered rowKey={(record, index) => record._id || index} />
          )}
        </Col>
      </Row>

      <Divider />

      <Row gutter={20}>
        <Col span={24}>
          <Typography.Title level={4}>Business Names</Typography.Title>
        </Col>

        {!readMode && (
          <Col span={24}>
            <Formik
              initialValues={{
                businessName: ""
              }}
              validationSchema={businessNameValidationSchema}
              onSubmit={handleAddBusinessName}
            >
              {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
                <Row gutter={20}>
                  <Col span={18}>
                    <FormInput
                      containerClassname="mb-0"
                      placeholder="Add Business Name e.g. SMSBump"
                      error={errors.businessName}
                      touched={touched.businessName}
                      label="Business Name"
                      value={values.businessName}
                      onChange={handleChange}
                      name="businessName"
                      size="default"
                    />
                  </Col>
                  <Col span={6}>
                    <div style={{ marginTop: "52px" }}>
                      <AppButton label="Add" type="button" loading={isSubmitting} prefixIcon={<i className="fas fa-plus me-2" />} onClick={handleSubmit} light />
                    </div>
                  </Col>
                </Row>
              )}
            </Formik>
          </Col>
        )}

        <Col span={24}>
          {currentData.businessNames.length > 0 && (
            <AppTable
              columns={getBusinessNameColumns()}
              dataSource={currentData.businessNames.map((name, index) => ({
                name,
                key: index
              }))}
              pagination={false}
              bordered
              rowKey="key"
              light
            />
          )}
        </Col>
      </Row>

      <Divider />

      <Row gutter={20}>
        <Col span={24}>
          <Typography.Title level={4}>Personal Numbers</Typography.Title>
        </Col>

        {!readMode && (
          <Col span={24}>
            <Formik
              initialValues={{
                personalNumber: ""
              }}
              validationSchema={personalNumberValidationSchema}
              onSubmit={handleAddPersonalNumber}
            >
              {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
                <Row gutter={20}>
                  <Col span={18}>
                    <FormInput
                      containerClassname="mb-0"
                      placeholder="Add Personal Number e.g. 614xxxxxxxx"
                      error={errors.personalNumber}
                      touched={touched.personalNumber}
                      label="Personal Number"
                      value={values.personalNumber}
                      onChange={handleChange}
                      name="personalNumber"
                      size="default"
                    />
                  </Col>
                  <Col span={6}>
                    <div style={{ marginTop: "52px" }}>
                      <AppButton label="Add" type="button" loading={isSubmitting} prefixIcon={<i className="fas fa-plus me-2" />} onClick={handleSubmit} light />
                    </div>
                  </Col>
                </Row>
              )}
            </Formik>
          </Col>
        )}

        <Col span={24}>
          {currentData.personalNumbers.length > 0 && (
            <AppTable
              columns={getPersonalNumberColumns()}
              dataSource={currentData.personalNumbers.map((number, index) => ({
                number,
                key: index
              }))}
              pagination={false}
              bordered
              rowKey="key"
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default inject(stores => ({
  userStore: stores.store.userStore
}))(observer(SenderIdForm));
